import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogOut, Package, FolderTree, Image, Upload, Home, Download, UploadCloud } from 'lucide-react';
import AdminProducts from '../components/AdminProducts';
import AdminCategories from '../components/AdminCategories';
import AdminBanners from '../components/AdminBanners';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [activeTab, setActiveTab] = useState('products');
  const navigate = useNavigate();

  useEffect(() => {
    const savedToken = localStorage.getItem('adminToken');
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      });
      const { token: newToken } = response.data;
      setToken(newToken);
      localStorage.setItem('adminToken', newToken);
      setIsLoggedIn(true);
      toast.success('Login realizado com sucesso!');
    } catch (error) {
      toast.error('Credenciais inválidas');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsLoggedIn(false);
    setToken('');
    navigate('/');
  };

  const handleBackup = async () => {
    try {
      const response = await axios.get(`${API}/admin/backup`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `margareth_backup_${new Date().toISOString().slice(0,10)}.db`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao fazer backup');
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!window.confirm('ATENÇÃO: Isso vai substituir TODOS os dados atuais pelo backup. Deseja continuar?')) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/admin/restore`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Backup restaurado! Recarregando página...');
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao restaurar backup');
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/products/import-csv`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar CSV');
    }
  };

  const tabs = [
    { id: 'products', label: 'Produtos', icon: Package },
    { id: 'categories', label: 'Categorias', icon: FolderTree },
    { id: 'banners', label: 'Banners', icon: Image }
  ];

  if (!isLoggedIn) {
    return (
      <div 
        style={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '1rem',
          backgroundColor: '#FAFAFA' 
        }}
      >
        <div 
          style={{ 
            backgroundColor: '#fff', 
            padding: '2rem', 
            borderRadius: '12px', 
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            width: '100%',
            maxWidth: '400px'
          }}
        >
          <h1 
            style={{ 
              fontSize: '1.75rem', 
              marginBottom: '1.5rem', 
              textAlign: 'center',
              fontFamily: 'Playfair Display, serif' 
            }}
          >
            Área Administrativa
          </h1>
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <Label htmlFor="username">Usuário</Label>
              <Input
                id="username"
                data-testid="admin-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" style={{ width: '100%', marginTop: '0.5rem' }} data-testid="admin-login-button">
              Entrar
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FAFAFA' }}>
      {/* Header */}
      <header style={{ backgroundColor: '#000', color: '#fff', padding: '0.75rem 1rem' }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <img
              src="/logo-margareth-white.png"
              alt="Margareth Bernardo"
              style={{ height: '32px', objectFit: 'contain' }}
            />
            <span style={{ 
              fontSize: '1rem', 
              fontFamily: 'Playfair Display, serif', 
              color: '#E8DCC4',
              display: 'none'
            }}
            className="admin-title"
            >
              Admin
            </span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleBackup}
              style={{ color: '#22c55e', padding: '0.5rem' }}
              data-testid="backup-button"
              title="Fazer backup"
            >
              <Download className="w-4 h-4" />
              <span style={{ marginLeft: '0.25rem', display: 'none' }} className="btn-text">Backup</span>
            </Button>
            <label>
              <input
                type="file"
                accept=".db"
                onChange={handleRestore}
                style={{ display: 'none' }}
                data-testid="restore-input"
              />
              <Button 
                variant="ghost" 
                size="sm"
                onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="restore-input"]').click(); }}
                style={{ color: '#f59e0b', padding: '0.5rem' }}
                data-testid="restore-button"
                title="Restaurar backup"
              >
                <UploadCloud className="w-4 h-4" />
                <span style={{ marginLeft: '0.25rem', display: 'none' }} className="btn-text">Restaurar</span>
              </Button>
            </label>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/')}
              style={{ color: '#E8DCC4', padding: '0.5rem' }}
              data-testid="go-to-site-button"
            >
              <Home className="w-4 h-4" />
              <span style={{ marginLeft: '0.25rem', display: 'none' }} className="btn-text">Ver Site</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              style={{ color: '#fff', padding: '0.5rem' }}
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span style={{ marginLeft: '0.25rem', display: 'none' }} className="btn-text">Sair</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Tab Navigation - Mobile First */}
      <nav style={{ 
        backgroundColor: '#fff', 
        borderBottom: '1px solid #eee',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto',
          display: 'flex',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: '1',
                  minWidth: 'fit-content',
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  border: 'none',
                  backgroundColor: 'transparent',
                  borderBottom: isActive ? '3px solid #000' : '3px solid transparent',
                  color: isActive ? '#000' : '#666',
                  fontWeight: isActive ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
                data-testid={`tab-${tab.id}`}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* CSV Import (only for products tab) */}
      {activeTab === 'products' && (
        <div style={{ 
          backgroundColor: '#fff', 
          borderBottom: '1px solid #eee',
          padding: '0.75rem 1rem'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <label style={{ cursor: 'pointer' }}>
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                style={{ display: 'none' }}
                data-testid="csv-upload-input"
              />
              <Button 
                type="button" 
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('[data-testid="csv-upload-input"]').click();
                }}
                data-testid="csv-upload-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                Importar CSV
              </Button>
            </label>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '1.5rem 1rem' }}>
        {activeTab === 'products' && <AdminProducts token={token} />}
        {activeTab === 'categories' && <AdminCategories token={token} />}
        {activeTab === 'banners' && <AdminBanners token={token} />}
      </main>

      {/* Mobile-first responsive styles */}
      <style>{`
        @media (min-width: 640px) {
          .admin-title {
            display: inline !important;
          }
          .btn-text {
            display: inline !important;
          }
        }
        
        nav::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default Admin;
